import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BugReportButton } from "@/components/BugReportButton";
import { BrandMark } from "@/components/Brand";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import PromptGenerator from "@/pages/PromptGenerator";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordResetPages";
import SharePage from "@/pages/SharePage";
import PricingPage from "@/pages/PricingPage";
import AdminInbox from "@/pages/AdminInbox";
import BillingPage from "@/pages/BillingPage";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentPages";

export function Loader() {
  return (
    <div className="stp-bg flex min-h-screen items-center justify-center" data-testid="app-loader">
      <div className="animate-pulse"><BrandMark size={48} /></div>
    </div>
  );
}

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<GuestOnly><AuthPage /></GuestOnly>} />
      <Route path="/app" element={<Protected><PromptGenerator /></Protected>} />
      <Route path="/admin" element={<Protected admin><AdminInbox /></Protected>} />
      <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/p/:id" element={<SharePage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <BugReportButton />
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="top-center"
        mobileOffset={{ top: 76 }}
        theme="light"
        toastOptions={{ style: { borderRadius: "16px", border: "1px solid rgba(0,0,0,0.06)", fontFamily: "Inter, sans-serif", color: "#1D1D1F" } }}
      />
    </div>
  );
}

export default App;
